import React, { useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import {
  Box, Typography, Table, TableHead, TableRow, TableCell, Button, Stack, Chip, IconButton, Tooltip, Alert, CircularProgress,
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, MenuItem, Switch, FormControlLabel, InputAdornment, Paper,
} from '@mui/material';
import TableBody from '../components/PagedTableBody'; // pages rows beyond 10
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import {
  ALL_WISHLIST_TEMPLATES_QUERY,
  CREATE_WISHLIST_TEMPLATE_MUTATION,
  UPDATE_WISHLIST_TEMPLATE_MUTATION,
  DELETE_WISHLIST_TEMPLATE_MUTATION,
} from '../graphql/queries';

const TRADITIONS = ['iyer','iyengar','mudaliar','vellalar','naidu','general','other'];
const SCALES     = ['intimate','medium','grand','royal'];
const PRIORITIES = ['low','medium','high','critical'];

const EMPTY = { name: '', tradition: '', eventScale: '', priority: 'medium', items: [], isActive: true };

export default function AdminWishlistTemplatesPage() {
  const [dialog, setDialog]   = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm]       = useState(EMPTY);
  const [newTask, setNewTask] = useState('');
  const [newCost, setNewCost] = useState('');
  const [snack, setSnack]     = useState({ open: false, msg: '', ok: true });

  const { data, loading, refetch } = useQuery(ALL_WISHLIST_TEMPLATES_QUERY, { fetchPolicy: 'cache-and-network' });

  const toast = (msg, ok = true) => setSnack({ open: true, msg, ok });

  const [create, { loading: creating }] = useMutation(CREATE_WISHLIST_TEMPLATE_MUTATION, {
    onCompleted: () => { closeDialog(); refetch(); toast('Template created.'); },
    onError: e => toast(e.message, false),
  });
  const [update, { loading: updating }] = useMutation(UPDATE_WISHLIST_TEMPLATE_MUTATION, {
    onCompleted: () => { closeDialog(); refetch(); toast('Template updated.'); },
    onError: e => toast(e.message, false),
  });
  const [remove] = useMutation(DELETE_WISHLIST_TEMPLATE_MUTATION, {
    onCompleted: () => { refetch(); toast('Template deleted.'); },
    onError: e => toast(e.message, false),
  });

  const templates = data?.allWishlistTemplates || [];

  const openCreate = () => { setEditing(null); setForm(EMPTY); setDialog(true); };
  const openEdit   = (t) => {
    setEditing(t);
    setForm({ name: t.name, tradition: t.tradition || '', eventScale: t.eventScale || '', priority: t.priority || 'medium', items: t.items || [], isActive: t.isActive });
    setDialog(true);
  };
  const closeDialog = () => { setDialog(false); setEditing(null); setForm(EMPTY); setNewTask(''); setNewCost(''); };

  const addItem = () => {
    if (!newTask.trim()) return;
    setForm(f => ({ ...f, items: [...(f.items || []), { task: newTask.trim(), done: false, cost: Number(newCost) || 0 }] }));
    setNewTask('');
    setNewCost('');
  };
  const removeItem = (idx) => setForm(f => ({ ...f, items: f.items.filter((_, i) => i !== idx) }));
  const updateItemCost = (idx, cost) =>
    setForm(f => ({ ...f, items: f.items.map((it, i) => i === idx ? { ...it, cost: Number(cost) || 0 } : it) }));

  const templateTotal = (items) => (items || []).reduce((sum, it) => sum + (Number(it.cost) || 0), 0);

  const handleSave = () => {
    if (!form.name.trim()) return;
    const vars = { ...form, items: form.items };
    if (editing) update({ variables: { id: editing.id, ...vars } });
    else create({ variables: vars });
  };

  return (
    <Box p={3}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Box>
          <Typography variant="h4" fontWeight={700}>Wishlist Templates</Typography>
          <Typography variant="body2" color="text.secondary">Pre-built checklist templates for different traditions</Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>New Template</Button>
      </Stack>

      {snack.open && (
        <Alert severity={snack.ok ? 'success' : 'error'} onClose={() => setSnack(s => ({ ...s, open: false }))} sx={{ mb: 2 }}>
          {snack.msg}
        </Alert>
      )}

      {loading ? <CircularProgress /> : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Tradition</TableCell>
              <TableCell>Scale</TableCell>
              <TableCell>Priority</TableCell>
              <TableCell>Items</TableCell>
              <TableCell>Est. Cost</TableCell>
              <TableCell>Active</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {templates.map(t => (
              <TableRow key={t.id} hover>
                <TableCell>{t.name}</TableCell>
                <TableCell>{t.tradition || '—'}</TableCell>
                <TableCell>{t.eventScale || '—'}</TableCell>
                <TableCell><Chip label={t.priority} size="small" color={t.priority === 'high' ? 'error' : t.priority === 'critical' ? 'error' : 'default'} /></TableCell>
                <TableCell>{(t.items || []).length}</TableCell>
                <TableCell>₹{templateTotal(t.items).toLocaleString('en-IN')}</TableCell>
                <TableCell><Chip label={t.isActive ? 'Active' : 'Inactive'} size="small" color={t.isActive ? 'success' : 'default'} /></TableCell>
                <TableCell align="right">
                  <Tooltip title="Edit"><IconButton size="small" onClick={() => openEdit(t)}><EditIcon fontSize="small" /></IconButton></Tooltip>
                  <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => remove({ variables: { id: t.id } })}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                </TableCell>
              </TableRow>
            ))}
            {templates.length === 0 && (
              <TableRow><TableCell colSpan={8} align="center">No templates yet. Create the first one.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      )}

      <Dialog open={dialog} onClose={closeDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? 'Edit Template' : 'New Wishlist Template'}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} mt={1}>
            <TextField label="Name" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} required fullWidth />
            <TextField select label="Tradition" value={form.tradition} onChange={e => setForm(f => ({ ...f, tradition: e.target.value }))} fullWidth>
              <MenuItem value="">— Any —</MenuItem>
              {TRADITIONS.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
            </TextField>
            <TextField select label="Event Scale" value={form.eventScale} onChange={e => setForm(f => ({ ...f, eventScale: e.target.value }))} fullWidth>
              <MenuItem value="">— Any —</MenuItem>
              {SCALES.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
            </TextField>
            <TextField select label="Priority" value={form.priority} onChange={e => setForm(f => ({ ...f, priority: e.target.value }))} fullWidth>
              {PRIORITIES.map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}
            </TextField>
            <FormControlLabel
              control={<Switch checked={form.isActive} onChange={e => setForm(f => ({ ...f, isActive: e.target.checked }))} />}
              label="Active"
            />

            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="subtitle2">Checklist Items ({(form.items || []).length})</Typography>
              <Typography variant="subtitle2" color="primary.main">
                Total: ₹{templateTotal(form.items).toLocaleString('en-IN')}
              </Typography>
            </Stack>
            <Stack spacing={0.75}>
              {(form.items || []).map((item, idx) => (
                <Paper key={idx} variant="outlined" sx={{ p: 1 }}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="body2" sx={{ flex: 1 }}>{item.task}</Typography>
                    <TextField
                      value={item.cost ?? 0}
                      onChange={e => updateItemCost(idx, e.target.value)}
                      type="number"
                      size="small"
                      sx={{ width: 130 }}
                      InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
                    />
                    <IconButton size="small" color="error" onClick={() => removeItem(idx)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                </Paper>
              ))}
            </Stack>
            <Stack direction="row" spacing={1}>
              <TextField label="Add item" value={newTask} onChange={e => setNewTask(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && addItem()} size="small" fullWidth />
              <TextField label="Cost (₹)" value={newCost} onChange={e => setNewCost(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && addItem()} type="number" size="small" sx={{ width: 130 }} />
              <Button variant="outlined" onClick={addItem}>Add</Button>
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={creating || updating} startIcon={<SaveIcon />}>
            {editing ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
